import {
  DAYS_SHORT, MONTHS, COLORS, ICE_FACTORS, iceScore, iceTier, iceBarPercent, esc, slotToHHMM,
  formatDateTime, formatDuration, formatHMS, toast,
  formatMinutesShort, parseDurationInput, relativeDeadline, formatShortDate,
} from './config.js';
import {
  state, findBlock, findTask, taskClosedMs, taskTrackedMs,
  subtasksOf, isTopLevel, taskTreeClosedMs, taskTreeTrackedMs, isTreeRunning,
} from './state.js';
import { updateTaskAPI, createTaskAPI, deleteTaskAPI,
         loadTaskEntriesAPI, createTimeEntryAPI, updateTimeEntryAPI, deleteTimeEntryAPI } from './api.js';

// ══════════════════════════════════════════════════════════
// CALLBACK BRIDGE
// ══════════════════════════════════════════════════════════
let _cb = {};

export function registerSidebarCallbacks({ rerenderBlock, openEditModal, handleDeleteBlock, startTimer, stopTimer }) {
  _cb = { rerenderBlock, openEditModal, handleDeleteBlock, startTimer, stopTimer };
}

// Quina tasca té el panel ICE desplegat (null = cap). No es persisteix.
let iceExpandedFor = null;

// Pill d'ICE (score + tier) per a la fila col·lapsada i les targetes.
function icePillHTML(score) {
  const tier = iceTier(score);
  if (score == null || !tier) return `<span class="td-ice-empty">Sense puntuar</span>`;
  return `<span class="task-ice is-tier-${tier.id}">Score ${score}</span>`;
}

// Eco de la fórmula amb els valors actuals, p. ex. "I·C·F = 2·3·2".
function iceFormulaText(t) {
  const s = iceScore(t);
  if (s == null) return 'I·C·F';
  return `I·C·F = ${+t.ice_impact}·${+t.ice_confidence}·${+t.ice_ease}`;
}

// ══════════════════════════════════════════════════════════
// SMALL HELPERS
// ══════════════════════════════════════════════════════════
function dateOnly(v) {
  if (!v) return '';
  if (typeof v === 'string') return v.slice(0, 10);
  try { return new Date(v).toISOString().slice(0, 10); } catch { return ''; }
}

function deadlineBadgeHTML(deadline, done) {
  const iso = dateOnly(deadline);
  if (!iso) return '';
  const [y, m, d] = iso.split('-').map(Number);
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const dl = new Date(y, m - 1, d);
  const overdue = !done && dl < today;
  const label = `${d} ${MONTHS[m - 1].slice(0, 3)}`;
  return `<span class="task-deadline${overdue ? ' overdue' : ''}">🗓 ${label}</span>`;
}

// Sort by ICE score descending; tie-break by ice_ease descending; unscored tasks (null)
// sink to the bottom (Array.sort is stable so they keep their incoming order).
function byIceDesc(a, b) {
  const sa = iceScore(a) ?? -1, sb = iceScore(b) ?? -1;
  if (sb !== sa) return sb - sa;
  return (Number(b.ice_ease) || 0) - (Number(a.ice_ease) || 0);
}

// ══════════════════════════════════════════════════════════
// TASK CARD
// ══════════════════════════════════════════════════════════
// Right end of the card's single row: tracked time, then the play/stop button. The play
// button only shows on hover/focus (CSS) so the list stays quiet; while the timer runs the
// stop button and the live time stay visible.
//
// While the timer runs the time ticks live — timer.js updates every `.js-live-time` each
// second, adding the elapsed session to `data-base-ms` (which holds the CLOSED intervals only).
// A parent task shows its own time plus its subtasks', and counts as running when any of
// them is. The play button always times the task itself.
function taskMetaHTML(task, showTimer) {
  const ownRunning = !!state.runningEntry && state.runningEntry.task_id === task.id;
  const running = isTreeRunning(task);
  const trackedMs = taskTreeTrackedMs(task);

  let btnHTML = '';
  if (showTimer) {
    const cls = `task-timer-btn${running ? ' is-stop' : ''}`;
    const label = running ? 'Aturar' : 'Iniciar';
    // Si corre una subtasca, el ■ de la fila del pare l'atura (data-task-id = la que corre)
    const target = running && !ownRunning ? state.runningEntry.task_id : task.id;
    btnHTML = `<button class="${cls}" data-task-id="${esc(target)}" title="${label} el cronòmetre" aria-label="${label} el cronòmetre">${running ? '■' : '▶'}</button>`;
  }

  let timeHTML = '';
  if (running) {
    timeHTML = `<span class="task-time is-running" title="Cronòmetre en marxa"><span class="task-time-dot"></span><span class="js-live-time" data-base-ms="${taskTreeClosedMs(task)}">${formatHMS(trackedMs)}</span></span>`;
  } else if (trackedMs > 0) {
    timeHTML = `<span class="task-time" title="Temps acumulat">${formatDuration(trackedMs)}</span>`;
  }

  if (!btnHTML && !timeHTML) return '';
  return `<span class="task-card-meta">${timeHTML}${btnHTML}</span>`;
}

function taskCardHTML(task, opts = {}) {
  const { showUnassign = false, showAssign = false, blockId = null, showTimer = false } = opts;

  const dl = deadlineBadgeHTML(task.deadline, task.done);
  const score = iceScore(task);
  const tier = iceTier(score);
  const iceHTML = (score != null && tier)
    ? `<span class="task-ice is-tier-${tier.id}" title="Puntuació ICE · ${tier.label}">${score}</span>`
    : '';

  const subs = subtasksOf(task.id);
  const subHTML = subs.length
    ? `<span class="task-sub-count" title="Subtasques fetes">${subs.filter(t => t.done).length}/${subs.length}</span>`
    : '';

  let actionHTML = '';
  if (showUnassign) {
    actionHTML = `<button class="task-unassign-btn" data-task-id="${esc(task.id)}" title="Treure del bloc">✕</button>`;
  } else if (showAssign) {
    actionHTML = `<button class="task-assign-btn" data-task-id="${esc(task.id)}" data-block-id="${esc(blockId)}" title="Afegir al bloc">＋</button>`;
  }

  return `<div class="task-card${task.done ? ' is-done' : ''}" data-task-id="${esc(task.id)}" draggable="true">
    <button class="task-check${task.done ? ' checked' : ''}" data-task-id="${esc(task.id)}" title="${task.done ? 'Desmarcar' : 'Completar'}">${task.done ? '✓' : ''}</button>
    <div class="task-card-main">
      <div class="task-card-row">
        <span class="task-card-title">${esc(task.title)}</span>
        ${dl}
        ${iceHTML}
        ${subHTML}
        ${taskMetaHTML(task, showTimer)}
      </div>
    </div>
    ${actionHTML}
  </div>`;
}

// Wire checkbox + click-to-detail + drag on all task cards inside a container.
function attachTaskCardHandlers(container, afterChange) {
  container.querySelectorAll('.task-check').forEach(btn => {
    btn.addEventListener('click', async e => {
      e.stopPropagation();
      const id = btn.dataset.taskId;
      const task = findTask(id);
      if (!task) return;
      btn.disabled = true;
      const updated = await updateTaskAPI(id, { done: !task.done });
      if (!updated) { toast('Error completant la tasca'); btn.disabled = false; return; }
      afterChange ? afterChange() : renderSidebar();
    });
  });

  // Play / stop straight from the card. timer.js refreshes the tasks and re-renders the
  // sidebar afterwards, so there is nothing to call back here.
  container.querySelectorAll('.task-timer-btn').forEach(btn => {
    btn.addEventListener('click', async e => {
      e.stopPropagation();
      const id = btn.dataset.taskId;
      const tracking = !!state.runningEntry && state.runningEntry.task_id === id;
      btn.disabled = true;
      if (tracking) await _cb.stopTimer?.();
      else          await _cb.startTimer?.(id);
    });
  });

  container.querySelectorAll('.task-card').forEach(el => {
    el.addEventListener('click', e => {
      if (e.target.closest('.task-check') || e.target.closest('.task-unassign-btn')
          || e.target.closest('.task-assign-btn') || e.target.closest('.task-timer-btn')) return;
      selectTask(el.dataset.taskId);
    });
    el.addEventListener('dragstart', e => {
      const task = findTask(el.dataset.taskId);
      if (!task) return;
      state.pendingTaskDrop = task;
      e.dataTransfer.effectAllowed = 'copyMove';
      e.dataTransfer.setData('text/plain', task.id);
      el.classList.add('dragging');
    });
    el.addEventListener('dragend', () => {
      el.classList.remove('dragging');
      state.pendingTaskDrop = null;
    });
  });
}

// ══════════════════════════════════════════════════════════
// ROUTER
// ══════════════════════════════════════════════════════════
export function renderSidebar() {
  if (state.selectedTaskId)  { renderSidebarTaskDetail(state.selectedTaskId); return; }
  if (state.selectedBlockId) { renderSidebarBlockDetail(state.selectedBlockId); return; }
  if (state.selectedTipusId) { renderSidebarTipusDetail(state.selectedTipusId); return; }
  renderInbox();
}

// ══════════════════════════════════════════════════════════
// INBOX + TYPE SECTIONS
// ══════════════════════════════════════════════════════════
function renderInbox() {
  const topEl     = document.getElementById('sidebar-top');
  const container = document.getElementById('sidebar-tasks');
  if (!topEl || !container) return;

  const mode = state.sidebarMode || 'active';
  const inboxCount    = state.tasks.filter(t => isTopLevel(t) && !t.block_type_id && !t.done).length;
  const archivedCount = state.tasks.filter(t => isTopLevel(t) && t.done).length;

  topEl.innerHTML = `
    <div class="sidebar-header">
      <span class="sidebar-title">Tasques</span>
      ${mode === 'active' && inboxCount > 0 ? `<span class="sidebar-badge">${inboxCount}</span>` : ''}
    </div>
    <div class="sidebar-modes" role="tablist">
      <button class="sidebar-mode-btn${mode === 'active' ? ' is-active' : ''}" data-mode="active" role="tab">Actives</button>
      <button class="sidebar-mode-btn${mode === 'archive' ? ' is-active' : ''}" data-mode="archive" role="tab">
        Arxiu${archivedCount ? ` <span class="sidebar-mode-count">${archivedCount}</span>` : ''}
      </button>
    </div>
    ${mode === 'active' ? `<input class="task-add-input" id="task-quick-add" type="text" placeholder="+ Nova tasca…" autocomplete="off">` : ''}
  `;

  topEl.querySelectorAll('.sidebar-mode-btn').forEach(btn => {
    btn.addEventListener('click', () => {
      const next = btn.dataset.mode;
      if (state.sidebarMode === next) return;
      state.sidebarMode = next;
      renderInbox();
    });
  });

  if (mode === 'archive') {
    renderArchive(container);
    return;
  }

  const input = document.getElementById('task-quick-add');
  if (input) {
    input.addEventListener('keydown', async e => {
      e.stopPropagation();
      if (e.key !== 'Enter') return;
      const title = input.value.trim();
      if (!title) return;
      input.value = '';
      input.disabled = true;
      const task = await createTaskAPI({ title });
      input.disabled = false;
      if (!task) { toast('Error creant la tasca'); return; }
      renderInbox();
    });
    input.addEventListener('keyup', e => e.stopPropagation());
  }

  // Build sections: inbox first, then one per block type
  let html = sectionHTML('inbox', '📥 Safata d\'entrada', null,
    state.tasks.filter(t => isTopLevel(t) && !t.block_type_id && !t.done).sort(byIceDesc));

  state.blockTypes.forEach(tp => {
    const tasks = state.tasks.filter(t => isTopLevel(t) && t.block_type_id === tp.id && !t.done).sort(byIceDesc);
    const label = `${tp.emoji || '•'} ${tp.label}`;
    html += sectionHTML(tp.id, label, tp, tasks);
  });

  html += daySummaryHTML();

  container.innerHTML = html;

  wireSections(container);
  attachTaskCardHandlers(container, renderInbox);
}

// ══════════════════════════════════════════════════════════
// ARCHIVE (completed tasks, bucketed by recency)
// ══════════════════════════════════════════════════════════
function startOfDay(d) {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

function renderArchive(container) {
  const done = state.tasks.filter(t => isTopLevel(t) && t.done).slice().sort((a, b) => {
    const ta = new Date(a.done_at || a.created_at || 0).getTime();
    const tb = new Date(b.done_at || b.created_at || 0).getTime();
    return tb - ta;
  });

  if (done.length === 0) {
    container.innerHTML = `<div class="sidebar-empty">Encara cap tasca completada.<br><span class="sidebar-empty-hint">Quan completis tasques, apareixeran aquí per si en vols recuperar alguna.</span></div>`;
    return;
  }

  const today     = startOfDay(new Date());
  const yesterday = new Date(today); yesterday.setDate(today.getDate() - 1);
  const weekAgo   = new Date(today); weekAgo.setDate(today.getDate() - 7);

  const buckets = { today: [], yesterday: [], week: [], older: [] };
  for (const t of done) {
    const at = t.done_at ? new Date(t.done_at) : null;
    if (!at) { buckets.older.push(t); continue; }
    const day = startOfDay(at);
    if      (day.getTime() === today.getTime())     buckets.today.push(t);
    else if (day.getTime() === yesterday.getTime()) buckets.yesterday.push(t);
    else if (day.getTime() >= weekAgo.getTime())    buckets.week.push(t);
    else                                            buckets.older.push(t);
  }

  const order = [
    ['today',     'Avui'],
    ['yesterday', 'Ahir'],
    ['week',      'Aquesta setmana'],
    ['older',     'Anteriors'],
  ];

  let html = '';
  for (const [key, label] of order) {
    const tasks = buckets[key];
    if (!tasks.length) continue;
    html += `<div class="archive-bucket">
      <div class="archive-bucket-head">
        <span class="archive-bucket-label">${esc(label)}</span>
        <span class="archive-bucket-count">${tasks.length}</span>
      </div>
      <div class="archive-bucket-body">${tasks.map(t => taskCardHTML(t)).join('')}</div>
    </div>`;
  }

  container.innerHTML = html;
  attachTaskCardHandlers(container, renderInbox);
}

// ══════════════════════════════════════════════════════════
// DAY SUMMARY ("Fet avui") — footer of active inbox
// ══════════════════════════════════════════════════════════
function isToday(d) {
  if (!d) return false;
  const x = new Date(d);
  if (isNaN(x)) return false;
  const t = new Date();
  return x.getFullYear() === t.getFullYear()
      && x.getMonth()    === t.getMonth()
      && x.getDate()     === t.getDate();
}

function daySummaryHTML() {
  const doneToday = state.tasks.filter(t => isTopLevel(t) && t.done && isToday(t.done_at));
  doneToday.sort((a, b) => new Date(b.done_at).getTime() - new Date(a.done_at).getTime());

  const cards = doneToday.length
    ? doneToday.map(t => taskCardHTML(t)).join('')
    : `<div class="day-summary-empty">Encara cap tasca feta avui — endavant!</div>`;

  return `<div class="day-summary">
    <div class="day-summary-head">
      <span class="day-summary-icon">✓</span>
      <span class="day-summary-label">Fet avui</span>
      <span class="day-summary-count">${doneToday.length}</span>
    </div>
    <div class="day-summary-body">${cards}</div>
  </div>`;
}

const SECTION_TASK_LIMIT = 5;

function sectionHTML(key, label, tipus, tasks) {
  const collapsed = !!state.collapsedSections[key];
  const colorId = tipus && COLORS.find(c => c.id === tipus.color) ? tipus.color : null;
  const headStyle = colorId
    ? `background:var(--${colorId}-bg);color:var(--${colorId}-fg);`
    : `background:var(--surface-tint);color:var(--text-2);`;

  let body = '';
  if (!collapsed) {
    if (tasks.length === 0) {
      body = `<div class="task-section-empty">Cap tasca</div>`;
    } else {
      const truncated = tipus && tasks.length > SECTION_TASK_LIMIT;
      const visible = truncated ? tasks.slice(0, SECTION_TASK_LIMIT) : tasks;
      body = visible.map(t => taskCardHTML(t, { showTimer: true })).join('');
      if (truncated) {
        body += `<button class="task-section-more" data-tipus-id="${esc(tipus.id)}">Veure totes (${tasks.length})</button>`;
      }
    }
  }
  const isTipus = !!tipus;
  const openBtn = isTipus
    ? `<button class="task-section-open" data-tipus-id="${esc(tipus.id)}" title="Veure detall">›</button>`
    : '';
  const addBtn = isTipus
    ? `<button class="task-section-add" data-tipus-id="${esc(tipus.id)}" title="Afegir tasca a aquest tipus">＋</button>`
    : '';
  const labelEl = isTipus
    ? `<button class="task-section-label is-clickable" data-tipus-id="${esc(tipus.id)}" title="Veure detall">${esc(label)}</button>`
    : `<span class="task-section-label">${esc(label)}</span>`;
  return `<div class="task-section" data-section="${esc(key)}">
    <div class="task-section-header" data-section="${esc(key)}" style="${headStyle}">
      <button class="task-section-caret${collapsed ? ' collapsed' : ''}" data-section="${esc(key)}" title="${collapsed ? 'Desplegar' : 'Plegar'}">▾</button>
      ${labelEl}
      <span class="task-section-count">${tasks.length}</span>
      ${addBtn}
      ${openBtn}
    </div>
    <div class="task-section-body">${body}</div>
  </div>`;
}

function wireSections(container) {
  // Plegar / desplegar — només el caret
  container.querySelectorAll('.task-section-caret').forEach(caret => {
    caret.addEventListener('click', e => {
      e.stopPropagation();
      const key = caret.dataset.section;
      state.collapsedSections[key] = !state.collapsedSections[key];
      renderInbox();
    });
  });

  // Fletxa i nom de la secció → obrir detall del tipus
  container.querySelectorAll('.task-section-open, .task-section-label.is-clickable').forEach(el => {
    el.addEventListener('click', e => {
      e.stopPropagation();
      selectTipus(el.dataset.tipusId);
    });
  });

  // Botó ＋ → revela un input inline dins d'aquesta secció per crear tasca
  container.querySelectorAll('.task-section-add').forEach(btn => {
    btn.addEventListener('click', e => {
      e.stopPropagation();
      const tipusId = btn.dataset.tipusId;
      const section = btn.closest('.task-section');
      const body = section?.querySelector('.task-section-body');
      if (!body || body.querySelector('.section-add-input')) {
        body?.querySelector('.section-add-input')?.focus();
        return;
      }
      const input = document.createElement('input');
      input.className = 'section-add-input';
      input.type = 'text';
      input.placeholder = 'Títol de la tasca · Enter per crear';
      input.autocomplete = 'off';
      body.prepend(input);
      input.focus();

      const close = () => input.remove();
      input.addEventListener('keyup', ev => ev.stopPropagation());
      input.addEventListener('keydown', async ev => {
        ev.stopPropagation();
        if (ev.key === 'Escape') { close(); return; }
        if (ev.key !== 'Enter') return;
        const title = input.value.trim();
        if (!title) { close(); return; }
        input.disabled = true;
        const created = await createTaskAPI({ title, block_type_id: tipusId });
        if (!created) { toast('Error creant la tasca'); input.disabled = false; return; }
        renderInbox();
      });
      input.addEventListener('blur', () => { if (!input.value.trim()) close(); });
    });
  });

  // "Veure totes" → open tipus detail view
  container.querySelectorAll('.task-section-more').forEach(btn => {
    btn.addEventListener('click', e => {
      e.stopPropagation();
      selectTipus(btn.dataset.tipusId);
    });
  });

  // Drop targets: dropping a task assigns its block_type_id
  container.querySelectorAll('.task-section').forEach(sec => {
    const key = sec.dataset.section;
    const tipusId = key === 'inbox' ? null : key;
    sec.addEventListener('dragover', e => {
      if (!state.pendingTaskDrop) return;
      e.preventDefault();
      e.dataTransfer.dropEffect = 'move';
      sec.classList.add('drop-hover');
    });
    sec.addEventListener('dragleave', () => sec.classList.remove('drop-hover'));
    sec.addEventListener('drop', async e => {
      sec.classList.remove('drop-hover');
      const task = state.pendingTaskDrop;
      state.pendingTaskDrop = null;
      if (!task) return;
      e.preventDefault();
      e.stopPropagation();
      if (task.block_type_id === tipusId) return;
      const updated = await updateTaskAPI(task.id, { block_type_id: tipusId });
      if (!updated) { toast('Error movent la tasca'); return; }
      renderInbox();
    });
  });
}

// ══════════════════════════════════════════════════════════
// TASK SELECTION
// ══════════════════════════════════════════════════════════
export function selectTask(id) {
  state.selectedTaskId = id;
  state.selectedBlockId = null;
  state.selectedTipusId = null;
  document.querySelectorAll('.block').forEach(el => el.classList.remove('block-selected'));
  renderSidebarTaskDetail(id);
}

export function deselectTask() {
  state.selectedTaskId = null;
  renderInbox();
}

export function selectTipus(id) {
  state.selectedTipusId = id;
  state.selectedTaskId = null;
  state.selectedBlockId = null;
  document.querySelectorAll('.block').forEach(el => el.classList.remove('block-selected'));
  renderSidebarTipusDetail(id);
}

export function deselectTipus() {
  state.selectedTipusId = null;
  renderInbox();
}

// ══════════════════════════════════════════════════════════
// TASK DETAIL PANEL (Asana-like)
// ══════════════════════════════════════════════════════════
export function renderSidebarTaskDetail(taskId) {
  const task = findTask(taskId);
  if (!task) { deselectTask(); return; }

  const topEl     = document.getElementById('sidebar-top');
  const container = document.getElementById('sidebar-tasks');
  if (!topEl || !container) return;

  // Una subtasca torna al detall del seu pare en lloc de la llista
  const parent = task.parent_id ? findTask(task.parent_id) : null;
  topEl.innerHTML = `
    <div class="td-topbar">
      <button class="td-back-btn" id="task-back-btn" title="Tornar">← ${parent ? esc(parent.title) : 'Tasques'}</button>
      <button class="td-trash-btn" id="td-delete" title="Eliminar tasca">🗑</button>
    </div>
  `;
  document.getElementById('task-back-btn')?.addEventListener('click', () => {
    if (parent) selectTask(parent.id);
    else deselectTask();
  });

  // Tipus: desplegable + punt de color
  const curTipus = state.blockTypes.find(tp => tp.id === task.block_type_id);
  const dotColor = curTipus && COLORS.find(c => c.id === curTipus.color) ? curTipus.color : null;
  const dotStyle = dotColor ? `background:var(--${dotColor}-bd);` : `background:var(--text-4);`;
  const tipusOptions = `<option value="">Sense tipus</option>` + state.blockTypes.map(tp =>
    `<option value="${esc(tp.id)}"${task.block_type_id === tp.id ? ' selected' : ''}>${esc((tp.emoji ? tp.emoji + ' ' : '') + tp.label)}</option>`
  ).join('');

  // ICE: estat col·lapsat + estat inicial dels 3 grups de botons (null = no puntuat)
  const score = iceScore(task);
  const tier = iceTier(score);
  const pct = iceBarPercent(score);
  const expanded = iceExpandedFor === taskId;

  const factorsHTML = ICE_FACTORS.map(f => {
    const cur = task[f.id] != null ? Number(task[f.id]) : null;
    const buttons = [1, 2, 3].map(v => `
      <button type="button" class="td-ice-btn${cur === v ? ' is-sel' : ''}"
              data-ice-factor="${f.id}" data-ice-val="${v}">${v}</button>
    `).join('');
    const descText = cur != null ? '· ' + f.values[cur] : 'Selecciona un valor';
    return `
      <div class="td-ice-factor" data-ice-factor-block="${f.id}">
        <div class="td-ice-factor-head">
          <span class="td-ice-factor-label">${f.label}</span>
          <span class="td-ice-factor-hint">${f.hint}</span>
          <span class="td-ice-factor-val${cur != null ? '' : ' is-unset'}" data-ice-cur="${f.id}">${cur != null ? cur : '—'}</span>
        </div>
        <div class="td-ice-btns">${buttons}</div>
        <div class="td-ice-factor-desc${cur != null ? '' : ' is-unset'}" data-ice-desc="${f.id}">${descText}</div>
      </div>
    `;
  }).join('');

  const dotPosStyle = pct != null ? `style="left:${pct}%"` : '';

  // "Programada a" (només si està assignada a un bloc)
  let schedHTML = '';
  const block = task.block_id ? findBlock(task.block_id) : null;
  if (block) {
    schedHTML = `<div class="td-meta-row td-meta-row--sched">
      <span class="td-meta-icon">📌</span>
      <span class="td-meta-label">Programada</span>
      <div class="td-meta-value td-sched">
        <span>${esc(DAYS_SHORT[block.day])} · ${slotToHHMM(block.startSlot)}–${slotToHHMM(block.startSlot + block.dur)} · ${esc(block.title)}</span>
        <button class="td-unschedule" title="Desprogramar">✕</button>
      </div>
    </div>`;
  }

  // Temps: total contra estimació (barra) + intervals agrupats per dia, sempre visibles
  const tracking = !!state.runningEntry && state.runningEntry.task_id === taskId;
  const tempsHTML = `
    <section class="td-time" id="td-temps">
      <div class="td-time-head">
        <span class="td-time-title">Temps</span>
        <button class="td-timer-btn${tracking ? ' is-stop' : ''}" id="td-timer-btn">${tracking ? '■ Aturar' : '▶ Iniciar'}</button>
      </div>
      <div class="td-time-summary">
        <span class="td-time-total" id="td-temps-total">—</span>
        <label class="td-time-est">estimació
          <input class="td-est-input" id="td-est" type="text" inputmode="text" autocomplete="off"
                 value="${esc(formatMinutesShort(task.estimate_min))}" placeholder="2h" title="Escriu 2h, 1h30 o 45m">
        </label>
      </div>
      <div class="td-time-split" id="td-time-split"></div>
      <div class="td-time-bar" id="td-time-bar" hidden>
        <div class="td-time-fill" id="td-time-fill"></div>
        <div class="td-time-mark" id="td-time-mark" title="Estimació"></div>
      </div>
      <div class="td-time-note" id="td-time-note"></div>
      <div class="td-time-subhead">
        <span>Intervals</span>
        <button class="td-link" id="te-add" type="button">+ Afegir temps</button>
      </div>
      <div class="te-form" id="te-form" hidden></div>
      <div class="td-temps-list" id="td-temps-list"></div>
    </section>`;

  const dlRel = relativeDeadline(dateOnly(task.deadline));

  // Subtasques (només a tasques de primer nivell): progrés, temps i llista; les obertes primer
  let subsHTML = '';
  if (!parent) {
    const subs = subtasksOf(taskId).slice().reverse()
      .sort((a, b) => Number(a.done) - Number(b.done));
    const doneN = subs.filter(t => t.done).length;
    const subsMs = subs.reduce((sum, t) => sum + taskTrackedMs(t), 0);
    subsHTML = `
      <section class="td-time td-subs">
        <div class="td-time-head">
          <span class="td-time-title">Subtasques${subs.length ? ` <span class="td-subs-count">${doneN} de ${subs.length}</span>` : ''}</span>
          ${subsMs > 0 ? `<span class="td-subs-time">${formatMinutesShort(subsMs / 60000)}</span>` : ''}
        </div>
        ${subs.length ? `<div class="td-subs-bar"><div style="width:${(doneN / subs.length) * 100}%"></div></div>` : ''}
        <div class="td-subs-list" id="td-subs-list">${subs.map(t => taskCardHTML(t, { showTimer: true })).join('')}</div>
        <button class="td-link td-subs-add" id="td-sub-add" type="button">+ Afegir subtasca</button>
        <input class="block-newtask-input" id="td-sub-input" type="text" placeholder="Títol de la subtasca · Enter per crear" hidden>
      </section>`;
  }

  container.innerHTML = `
    <div class="task-detail task-detail--v3${parent ? ' is-subtask' : ''}">
      <div class="td-hero">
        <div class="td-hero-titlerow">
          <button class="task-check${task.done ? ' checked' : ''}" id="td-check" title="${task.done ? 'Desmarcar' : 'Completar'}">${task.done ? '✓' : ''}</button>
          <textarea class="td-title-input${task.done ? ' is-done' : ''}" id="td-title" rows="1" placeholder="Títol de la tasca">${esc(task.title)}</textarea>
        </div>
        <textarea class="td-desc" id="td-desc" placeholder="Afegir una descripció…">${esc(task.description || '')}</textarea>
      </div>

      <div class="td-meta-rail">
        <div class="td-meta-row td-meta-row--tipus">
          <span class="td-meta-icon"><span class="td-tipus-dot" id="td-tipus-dot" style="${dotStyle}"></span></span>
          <span class="td-meta-label">Tipus</span>
          <div class="td-meta-value">
            <select class="td-select" id="td-tipus-select">${tipusOptions}</select>
          </div>
        </div>

        <div class="td-meta-row">
          <span class="td-meta-icon">🗓</span>
          <span class="td-meta-label">Data límit</span>
          <div class="td-meta-value td-deadline-val">
            <input class="td-row-input" id="td-deadline" type="date" value="${dateOnly(task.deadline)}">
            <span class="td-deadline-rel${dlRel?.overdue && !task.done ? ' is-overdue' : ''}" id="td-deadline-rel">${dlRel ? dlRel.text : ''}</span>
          </div>
        </div>

        ${schedHTML}

        <div class="td-ice${expanded ? ' is-expanded' : ''}" id="td-ice">
          <div class="td-meta-row td-meta-row--ice">
            <span class="td-meta-icon">📊</span>
            <span class="td-meta-label">ICE</span>
            <div class="td-meta-value td-ice-rowval">
              <span class="td-ice-pillwrap" id="td-ice-pillwrap">${icePillHTML(score)}</span>
              <button class="td-ice-toggle" id="td-ice-toggle">${expanded ? 'tanca' : 'desplega'}</button>
            </div>
          </div>

          <div class="td-ice-panel">
            <div class="td-ice-panel-head">
              <span class="td-ice-panel-title">SCORE ICE</span>
              <span class="td-ice-tier${tier ? ' is-tier-' + tier.id : ''}" id="td-ice-tier">${tier ? 'prioritat ' + tier.label : '—'}</span>
            </div>

            <div class="td-ice-scorerow">
              <div class="td-ice-bignum" id="td-ice-bignum">${score != null ? score : '—'}</div>
              <div class="td-ice-scorerow-meta">
                <div class="td-ice-tier-desc" id="td-ice-tier-desc">${tier ? tier.desc : ''}</div>
                <div class="td-ice-formula" id="td-ice-formula">${iceFormulaText(task)}</div>
              </div>
            </div>

            <div class="td-ice-bar${pct == null ? ' is-empty' : ''}" id="td-ice-bar">
              <div class="td-ice-bar-dot" id="td-ice-bar-dot" ${dotPosStyle}></div>
            </div>
            <div class="td-ice-bar-labels"><span>baixa</span><span>mitjana</span><span>alta</span></div>

            ${factorsHTML}

            <div class="td-ice-hint">score = camp calculat · no editable a mà<br>en cas d'empat, mana la Facilitat</div>
          </div>
        </div>
      </div>

      ${subsHTML}

      ${tempsHTML}

      ${task.created_at ? `<div class="td-created">Creada el ${formatDateTime(task.created_at)}</div>` : ''}
    </div>
  `;

  wireTaskDetail(taskId);
}

function wireTaskDetail(taskId) {
  // Done checkbox (re-render: canvia l'estil del títol)
  document.getElementById('td-check')?.addEventListener('click', async () => {
    const task = findTask(taskId);
    await updateTaskAPI(taskId, { done: !task.done });
    renderSidebarTaskDetail(taskId);
  });

  // Títol (textarea multilínia amb auto-grow; desa al canviar)
  const titleEl = document.getElementById('td-title');
  const autoGrow = () => {
    if (!titleEl) return;
    titleEl.style.height = 'auto';
    titleEl.style.height = titleEl.scrollHeight + 'px';
  };
  if (titleEl) autoGrow();
  titleEl?.addEventListener('input', autoGrow);
  titleEl?.addEventListener('keydown', e => {
    e.stopPropagation();
    // Enter (sense Shift) confirma el títol en lloc d'afegir salt de línia
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); titleEl.blur(); }
  });
  titleEl?.addEventListener('change', async () => {
    const v = titleEl.value.replace(/\s+/g, ' ').trim();
    if (!v) { titleEl.value = findTask(taskId)?.title || ''; autoGrow(); return; }
    await updateTaskAPI(taskId, { title: v });
  });

  // Tipus (desplegable; actualitza el punt de color in situ, sense re-render)
  const tipusSel = document.getElementById('td-tipus-select');
  tipusSel?.addEventListener('change', async () => {
    const next = tipusSel.value || null;
    await updateTaskAPI(taskId, { block_type_id: next });
    const tp = state.blockTypes.find(t => t.id === next);
    const colorId = tp && COLORS.find(c => c.id === tp.color) ? tp.color : null;
    const dot = document.getElementById('td-tipus-dot');
    if (dot) dot.style.background = colorId ? `var(--${colorId}-bd)` : 'var(--text-4)';
  });

  // Data límit
  const dlEl = document.getElementById('td-deadline');
  dlEl?.addEventListener('change', async () => {
    await updateTaskAPI(taskId, { deadline: dlEl.value || null });
    const rel = relativeDeadline(dlEl.value);
    const relEl = document.getElementById('td-deadline-rel');
    if (relEl) {
      relEl.textContent = rel ? rel.text : '';
      relEl.classList.toggle('is-overdue', !!rel?.overdue && !findTask(taskId)?.done);
    }
  });

  // Descripció (desa al canviar)
  const descEl = document.getElementById('td-desc');
  descEl?.addEventListener('keydown', e => e.stopPropagation());
  descEl?.addEventListener('change', async () => {
    await updateTaskAPI(taskId, { description: descEl.value.trim() || null });
  });

  // Temps (cronòmetre + estimació + intervals)
  wireTemps(taskId);

  // Subtasques
  wireSubtasks(taskId);

  // ICE (toggle + sliders amb recàlcul en viu)
  wireIce(taskId);

  // Desprogramar (clear block_id)
  document.querySelector('#task-sidebar .td-unschedule')?.addEventListener('click', async () => {
    const prevBlock = findTask(taskId)?.block_id;
    await updateTaskAPI(taskId, { block_id: null });
    if (prevBlock) _cb.rerenderBlock?.(prevBlock);
    renderSidebarTaskDetail(taskId);
  });

  // Eliminar tasca
  document.getElementById('td-delete')?.addEventListener('click', async () => {
    if (!confirm('Eliminar aquesta tasca?')) return;
    const ok = await deleteTaskAPI(taskId);
    if (!ok) { toast('Error eliminant la tasca'); return; }
    deselectTask();
  });
}

// ── Subtasques al detall de tasca ────────────────────────
function wireSubtasks(taskId) {
  const listEl = document.getElementById('td-subs-list');
  if (listEl) attachTaskCardHandlers(listEl, () => renderSidebarTaskDetail(taskId));

  // "+ Afegir subtasca" → input; Enter crea i deixa l'input obert per a la següent
  const addBtn = document.getElementById('td-sub-add');
  const input  = document.getElementById('td-sub-input');
  addBtn?.addEventListener('click', () => {
    addBtn.hidden = true;
    input.hidden = false;
    input.focus();
  });
  input?.addEventListener('keyup', e => e.stopPropagation());
  input?.addEventListener('keydown', async e => {
    e.stopPropagation();
    if (e.key === 'Escape') { input.hidden = true; addBtn.hidden = false; return; }
    if (e.key !== 'Enter') return;
    const title = input.value.trim();
    if (!title) return;
    input.disabled = true;
    const parent = findTask(taskId);
    const created = await createTaskAPI({ title, parent_id: taskId, block_type_id: parent?.block_type_id ?? null });
    if (!created) { toast('Error creant la subtasca'); input.disabled = false; return; }
    renderSidebarTaskDetail(taskId);
    const again = document.getElementById('td-sub-input');
    document.getElementById('td-sub-add').hidden = true;
    again.hidden = false;
    again.focus();
  });
  input?.addEventListener('blur', () => {
    if (!input.value.trim()) { input.hidden = true; addBtn.hidden = false; }
  });
}

// ── Temps (time tracking) al detall de tasca ─────────────
// Durada d'un interval en ms; els oberts (sense ended_at) compten fins ara.
function entryDurMs(e) {
  const start = new Date(e.started_at).getTime();
  const end = e.ended_at ? new Date(e.ended_at).getTime() : Date.now();
  return Math.max(0, end - start);
}

// Quants intervals es veuen abans de "Veure'ls tots" (per tasca, es manté entre re-renders).
const TE_VISIBLE = 6;
let teExpandedFor = null;

const hhmm = d => `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
const isoDay = d => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

function wireTemps(taskId) {
  // Start / Stop (delega a timer.js via callbacks; després re-renderitza el detall)
  document.getElementById('td-timer-btn')?.addEventListener('click', async () => {
    const tracking = !!state.runningEntry && state.runningEntry.task_id === taskId;
    if (tracking) await _cb.stopTimer?.();
    else          await _cb.startTimer?.(taskId);
    renderSidebarTaskDetail(taskId);
  });

  // Estimació en format lliure ("2h", "1h30", "45m"); es mostra normalitzada
  const estEl = document.getElementById('td-est');
  estEl?.addEventListener('keydown', e => {
    e.stopPropagation();
    if (e.key === 'Enter') estEl.blur();
  });
  estEl?.addEventListener('change', async () => {
    const min = parseDurationInput(estEl.value);
    if (Number.isNaN(min)) {
      toast('No entenc la durada. Prova 2h, 1h30 o 45m');
      estEl.value = formatMinutesShort(findTask(taskId)?.estimate_min);
      return;
    }
    estEl.value = formatMinutesShort(min);
    await updateTaskAPI(taskId, { estimate_min: min });
    refreshTempsList(taskId);
  });

  document.getElementById('te-add')?.addEventListener('click', () => openEntryForm(taskId, null));

  refreshTempsList(taskId);
}

// Recarrega els intervals: total, barra contra l'estimació i llista per dies.
async function refreshTempsList(taskId) {
  const entries = await loadTaskEntriesAPI(taskId);
  const ownClosedMs = entries.filter(e => e.ended_at).reduce((sum, e) => sum + entryDurMs(e), 0);
  const ownMs       = entries.reduce((sum, e) => sum + entryDurMs(e), 0);

  // Una tasca pare compta també el temps de les seves subtasques
  const task = findTask(taskId);
  const subs = isTopLevel(task) ? subtasksOf(taskId) : [];
  const subsMs = subs.reduce((sum, t) => sum + taskTrackedMs(t), 0);
  const subsClosedMs = subs.reduce((sum, t) => sum + taskClosedMs(t), 0);
  const subRunning = subs.some(t => state.runningEntry?.task_id === t.id);

  const closedMs = ownClosedMs + subsClosedMs;
  const totalMs  = ownMs + subsMs;
  const running  = entries.some(e => !e.ended_at) || subRunning;

  const splitEl = document.getElementById('td-time-split');
  if (splitEl) {
    splitEl.textContent = subsMs > 0
      ? `${formatMinutesShort(ownMs / 60000)} propi + ${formatMinutesShort(subsMs / 60000)} de subtasques`
      : '';
  }

  const totalEl = document.getElementById('td-temps-total');
  if (totalEl) {
    // En marxa: el total corre en viu (timer.js actualitza .js-live-time cada segon)
    totalEl.innerHTML = running
      ? `<span class="js-live-time" data-base-ms="${closedMs}">${formatHMS(totalMs)}</span>`
      : (totalMs > 0 ? formatMinutesShort(totalMs / 60000) : 'Sense temps');
    totalEl.classList.toggle('is-running', running);
    totalEl.classList.toggle('is-empty', !running && totalMs <= 0);
  }

  renderTimeBar(taskId, totalMs);

  const listEl = document.getElementById('td-temps-list');
  if (listEl) renderTempsEntries(taskId, entries, listEl);
}

// Barra: el temps real sobre una escala que inclou l'estimació; la marca és l'estimació.
function renderTimeBar(taskId, totalMs) {
  const bar  = document.getElementById('td-time-bar');
  const note = document.getElementById('td-time-note');
  if (!bar || !note) return;
  const estMin = findTask(taskId)?.estimate_min;
  if (!estMin) { bar.hidden = true; note.textContent = ''; return; }

  const estMs = estMin * 60000;
  const scale = Math.max(totalMs, estMs);
  bar.hidden = false;
  document.getElementById('td-time-fill').style.width = `${(totalMs / scale) * 100}%`;
  document.getElementById('td-time-mark').style.left  = `${(estMs / scale) * 100}%`;

  const over = totalMs > estMs;
  bar.classList.toggle('is-over', over);
  note.classList.toggle('is-over', over);
  note.textContent = over
    ? `+${formatMinutesShort((totalMs - estMs) / 60000)} sobre l'estimació (${Math.round((totalMs / estMs) * 100)}%)`
    : `Queden ${formatMinutesShort((estMs - totalMs) / 60000)}`;
}

function renderTempsEntries(taskId, entries, listEl) {
  const sorted = [...entries].sort((a, b) => new Date(b.started_at) - new Date(a.started_at));
  const expanded = teExpandedFor === taskId;
  const visible = expanded ? sorted : sorted.slice(0, TE_VISIBLE);

  let lastDay = '';
  const rows = visible.map(e => {
    const start = new Date(e.started_at);
    const day = isoDay(start);
    const dayLabel = day === lastDay ? '' : formatShortDate(start);
    lastDay = day;

    let range;
    if (!e.ended_at) {
      range = `<span class="te-range is-running"><span class="task-time-dot"></span>${hhmm(start)} – ara</span>`;
    } else {
      const end = new Date(e.ended_at);
      const nextDay = isoDay(end) !== day ? ' <small>+1</small>' : '';
      range = `<button class="te-range" type="button" title="Editar l'interval">${hhmm(start)}–${hhmm(end)}${nextDay}</button>`;
    }
    return `
      <div class="te-row" data-entry="${esc(e.id)}">
        <span class="te-day">${dayLabel}</span>
        ${range}
        <span class="te-dur">${formatMinutesShort(entryDurMs(e) / 60000)}</span>
        <button class="te-del" type="button" title="Eliminar l'interval" aria-label="Eliminar l'interval">✕</button>
      </div>`;
  }).join('');

  const more = sorted.length > TE_VISIBLE
    ? `<button class="td-link te-more" type="button">${expanded ? 'Mostrar menys' : `Veure'ls tots (${sorted.length})`}</button>`
    : '';

  listEl.innerHTML = rows
    ? rows + more
    : `<div class="te-empty">Encara no hi ha temps. Prem ▶ Iniciar o afegeix-lo a mà.</div>`;

  listEl.querySelector('.te-more')?.addEventListener('click', () => {
    teExpandedFor = expanded ? null : taskId;
    renderTempsEntries(taskId, entries, listEl);
  });

  listEl.querySelectorAll('.te-del').forEach(btn => {
    btn.addEventListener('click', async () => {
      const id = btn.closest('.te-row').dataset.entry;
      if (!confirm('Eliminar aquest interval?')) return;
      await deleteTimeEntryAPI(id);
      refreshTempsList(taskId);
    });
  });

  listEl.querySelectorAll('button.te-range').forEach(btn => {
    btn.addEventListener('click', () => {
      const id = btn.closest('.te-row').dataset.entry;
      openEntryForm(taskId, entries.find(x => x.id === id));
    });
  });
}

// Formulari inline per crear/editar un interval: dia + hora d'inici + hora de fi.
// Si la fi és anterior a l'inici, s'entén que acaba l'endemà.
function openEntryForm(taskId, entry) {
  const formEl = document.getElementById('te-form');
  if (!formEl) return;
  const now = new Date();
  const start = entry ? new Date(entry.started_at) : new Date(now.getTime() - 30 * 60000);
  const end   = entry ? new Date(entry.ended_at)   : now;

  formEl.hidden = false;
  formEl.innerHTML = `
    <div class="te-form-title">${entry ? 'Editar interval' : 'Afegir temps'}</div>
    <div class="te-form-grid">
      <label class="te-form-field">Dia <input type="date" id="te-day" value="${isoDay(start)}"></label>
      <label class="te-form-field">Inici <input type="time" id="te-start" value="${hhmm(start)}"></label>
      <label class="te-form-field">Fi <input type="time" id="te-end" value="${hhmm(end)}"></label>
    </div>
    <div class="te-form-actions">
      <button class="btn btn-ghost" id="te-cancel" type="button">Cancel·lar</button>
      <button class="btn btn-accent" id="te-save" type="button">Desar</button>
    </div>
  `;
  formEl.querySelectorAll('input').forEach(i => i.addEventListener('keydown', ev => ev.stopPropagation()));
  formEl.querySelector('#te-start').focus();

  const close = () => { formEl.hidden = true; formEl.innerHTML = ''; };
  formEl.querySelector('#te-cancel').addEventListener('click', close);

  formEl.querySelector('#te-save').addEventListener('click', async () => {
    const day = formEl.querySelector('#te-day').value;
    const sv  = formEl.querySelector('#te-start').value;
    const ev  = formEl.querySelector('#te-end').value;
    if (!day || !sv || !ev) { toast('Cal el dia, l\'inici i la fi'); return; }
    const s = new Date(`${day}T${sv}`);
    const e = new Date(`${day}T${ev}`);
    if (e <= s) e.setDate(e.getDate() + 1);

    const patch = { started_at: s.toISOString(), ended_at: e.toISOString() };
    if (entry) await updateTimeEntryAPI(entry.id, patch);
    else       await createTimeEntryAPI({ task_id: taskId, ...patch });
    close();
    refreshTempsList(taskId);
  });
}

function wireIce(taskId) {
  const iceEl = document.getElementById('td-ice');
  const toggle = document.getElementById('td-ice-toggle');
  if (!iceEl || !toggle) return;

  // Desplega / col·lapsa (sense re-render)
  toggle.addEventListener('click', () => {
    const isOpen = iceEl.classList.toggle('is-expanded');
    iceExpandedFor = isOpen ? taskId : null;
    toggle.textContent = isOpen ? 'tanca' : 'desplega';
  });

  // Còpia de treball en viu (null es manté fins que es clica un botó)
  const task = findTask(taskId);
  const live = {};
  ICE_FACTORS.forEach(f => { live[f.id] = task[f.id]; });

  const $ = sel => iceEl.querySelector(sel);
  const bignum   = $('#td-ice-bignum');
  const tierEl   = $('#td-ice-tier');
  const tierDesc = $('#td-ice-tier-desc');
  const formula  = $('#td-ice-formula');
  const bar      = $('#td-ice-bar');
  const dot      = $('#td-ice-bar-dot');
  const pillwrap = $('#td-ice-pillwrap');

  // Recàlcul global (score + tier + barra + pill) des de la còpia en viu.
  function refresh() {
    const score = iceScore(live);
    const tier = iceTier(score);
    const pct = iceBarPercent(score);
    bignum.textContent = score != null ? score : '—';
    tierEl.textContent = tier ? 'prioritat ' + tier.label : '—';
    tierEl.className = 'td-ice-tier' + (tier ? ' is-tier-' + tier.id : '');
    tierDesc.textContent = tier ? tier.desc : '';
    formula.textContent = iceFormulaText(live);
    pillwrap.innerHTML = icePillHTML(score);
    if (pct != null) {
      bar.classList.remove('is-empty');
      dot.style.left = pct + '%';
    } else {
      bar.classList.add('is-empty');
    }
  }

  async function persist(field, value) {
    const ok = await updateTaskAPI(taskId, { [field]: value });
    if (!ok) toast('Error desant ICE');
  }

  // 3 grups de botons (Impacte / Confiança / Facilitat) amb selecció exclusiva.
  // Event delegation: un sol listener al panel atrapa tots els clics dels botons.
  iceEl.addEventListener('click', async (e) => {
    const btn = e.target.closest('.td-ice-btn');
    if (!btn) return;
    const factor = btn.dataset.iceFactor;
    const val = Number(btn.dataset.iceVal);
    const meta = ICE_FACTORS.find(f => f.id === factor);
    if (!meta) return;

    // Actualitza estat local i UI del grup (selecció + valor + descripció).
    live[factor] = val;
    iceEl.querySelectorAll(`.td-ice-btn[data-ice-factor="${factor}"]`).forEach(b => {
      b.classList.toggle('is-sel', Number(b.dataset.iceVal) === val);
    });
    const curEl = iceEl.querySelector(`[data-ice-cur="${factor}"]`);
    if (curEl) { curEl.textContent = val; curEl.classList.remove('is-unset'); }
    const descEl = iceEl.querySelector(`[data-ice-desc="${factor}"]`);
    if (descEl) { descEl.textContent = '· ' + meta.values[val]; descEl.classList.remove('is-unset'); }

    refresh();
    await persist(factor, val);
  });
}

// ══════════════════════════════════════════════════════════
// BLOCK SELECTION (grid block → sidebar detail)
// ══════════════════════════════════════════════════════════
export function selectBlock(id) {
  if (state.selectedBlockId === id && !state.selectedTaskId) return;
  state.selectedTaskId = null;
  state.selectedTipusId = null;
  state.selectedBlockId = id;

  document.querySelectorAll('.block').forEach(el => el.classList.remove('block-selected'));
  const el = document.querySelector(`.block[data-id="${id}"]`);
  if (el) el.classList.add('block-selected');

  renderSidebarBlockDetail(id);
}

export function deselectBlock() {
  state.selectedBlockId = null;
  document.querySelectorAll('.block').forEach(el => el.classList.remove('block-selected'));
  renderSidebar();
}

// ══════════════════════════════════════════════════════════
// SIDEBAR BLOCK DETAIL MODE
// ══════════════════════════════════════════════════════════
export function renderSidebarBlockDetail(blockId) {
  const block = findBlock(blockId);
  if (!block) { deselectBlock(); return; }

  const tipusDef = state.blockTypes.find(t => t.id === block.tipusId);
  const topEl    = document.getElementById('sidebar-top');
  const container = document.getElementById('sidebar-tasks');
  if (!topEl || !container) return;

  const colorId = tipusDef && COLORS.find(c => c.id === tipusDef.color) ? tipusDef.color : null;
  const headStyle = colorId
    ? `background:var(--${colorId}-bg);color:var(--${colorId}-fg);`
    : `background:var(--surface-tint);color:var(--text);`;
  const headLabel = tipusDef
    ? esc((tipusDef.emoji ? tipusDef.emoji + ' ' : '') + tipusDef.label)
    : esc(block.title);
  const descHTML = tipusDef?.description
    ? `<div class="tipus-description">${esc(tipusDef.description)}</div>`
    : '';

  // Aquest bloc en concret: dia i hora, títol propi (si n'hi ha), notes i tasques assignades.
  const date = new Date(state.currentMonday);
  date.setDate(date.getDate() + block.day);
  const whenHTML = `${DAYS_SHORT[block.day]} ${date.getDate()} ${MONTHS[date.getMonth()]} · ${slotToHHMM(block.startSlot)}–${slotToHHMM(block.startSlot + block.dur)}`;
  const ownTitle = tipusDef && block.title && block.title !== tipusDef.label
    ? `<div class="block-detail-own-title">${esc(block.title)}</div>` : '';
  const notesHTML = block.notes
    ? `<div class="block-detail-notes">${esc(block.notes)}</div>` : '';

  const blockTasks = state.tasks.filter(t => t.block_id === block.id).sort(byIceDesc);
  const blockTasksHTML = blockTasks.length
    ? `<div class="task-group-title" style="margin-top:12px;">En aquest bloc <span class="task-group-count">${blockTasks.length}</span></div>
       <div id="block-own-tasks">${blockTasks.map(t => taskCardHTML(t, { showTimer: true })).join('')}</div>`
    : '';

  topEl.innerHTML = `
    <div style="margin-bottom:10px;">
      <button class="filter-pill" id="sidebar-back-btn" style="font-size:11px;padding:3px 9px;">← Tasques</button>
    </div>
    <div class="block-detail-head" style="${headStyle}">
      <div class="block-detail-title">${headLabel}</div>
      <div class="block-detail-sub">${whenHTML}</div>
    </div>
    ${ownTitle}
    ${notesHTML}
    ${descHTML}
    ${blockTasksHTML}
  `;

  document.getElementById('sidebar-back-btn')?.addEventListener('click', deselectBlock);
  const ownTasksEl = document.getElementById('block-own-tasks');
  if (ownTasksEl) attachTaskCardHandlers(ownTasksEl, () => renderSidebarBlockDetail(blockId));

  _renderTipusTaskList(block.tipusId, container, new Set(blockTasks.map(t => t.id)));
}

// ══════════════════════════════════════════════════════════
// SIDEBAR TIPUS DETAIL MODE (opened from "Veure totes" in inbox)
// ══════════════════════════════════════════════════════════
export function renderSidebarTipusDetail(tipusId) {
  const tipusDef = state.blockTypes.find(t => t.id === tipusId);
  if (!tipusDef) { deselectTipus(); return; }

  const topEl    = document.getElementById('sidebar-top');
  const container = document.getElementById('sidebar-tasks');
  if (!topEl || !container) return;

  const colorId = COLORS.find(c => c.id === tipusDef.color) ? tipusDef.color : null;
  const headStyle = colorId
    ? `background:var(--${colorId}-bg);color:var(--${colorId}-fg);`
    : `background:var(--surface-tint);color:var(--text);`;
  const headLabel = esc((tipusDef.emoji ? tipusDef.emoji + ' ' : '') + tipusDef.label);
  const descHTML = tipusDef.description
    ? `<div class="tipus-description">${esc(tipusDef.description)}</div>`
    : '';

  topEl.innerHTML = `
    <div style="margin-bottom:10px;">
      <button class="filter-pill" id="sidebar-back-btn" style="font-size:11px;padding:3px 9px;">← Tasques</button>
    </div>
    <div class="block-detail-head" style="${headStyle}">
      <div class="block-detail-title">${headLabel}</div>
    </div>
    ${descHTML}
  `;

  document.getElementById('sidebar-back-btn')?.addEventListener('click', deselectTipus);

  _renderTipusTaskList(tipusId, container);
}

// ──────────────────────────────────────────────────────────
// Shared task-list renderer for block-detail and tipus-detail
// ──────────────────────────────────────────────────────────
function _renderTipusTaskList(tipusId, container, excludeIds = new Set()) {
  const tasks = state.tasks
    .filter(t => isTopLevel(t) && t.block_type_id === tipusId && !t.done && !excludeIds.has(t.id))
    .sort(byIceDesc);

  let html = '';
  html += `<div class="task-group-title" style="margin-top:4px;">Tasques${tasks.length ? ` <span class="task-group-count">${tasks.length}</span>` : ''}</div>`;
  html += `<div class="block-newtask" id="block-newtask">
    <button class="block-newtask-trigger" type="button" id="block-newtask-trigger">＋ Nova tasca per aquest tipus</button>
    <input class="block-newtask-input" id="block-newtask-input" type="text" placeholder="Títol de la tasca · Enter per crear" hidden>
  </div>`;
  if (tasks.length === 0) {
    html += `<div class="task-section-empty">Cap tasca en aquest tipus.</div>`;
  } else {
    html += tasks.map(t => taskCardHTML(t, { showTimer: true })).join('');
  }

  container.innerHTML = html;

  // Inline "new task" — click trigger → input; Enter → create with this tipus
  const trigger = container.querySelector('#block-newtask-trigger');
  const input   = container.querySelector('#block-newtask-input');
  trigger?.addEventListener('click', () => {
    trigger.hidden = true;
    input.hidden = false;
    input.value = '';
    input.focus();
  });
  input?.addEventListener('keydown', e => e.stopPropagation());
  input?.addEventListener('keyup', e => e.stopPropagation());
  input?.addEventListener('keydown', async e => {
    if (e.key === 'Escape') {
      input.hidden = true;
      trigger.hidden = false;
      return;
    }
    if (e.key !== 'Enter') return;
    const title = input.value.trim();
    if (!title) return;
    input.disabled = true;
    const created = await createTaskAPI({ title, block_type_id: tipusId });
    if (!created) { toast('Error creant la tasca'); input.disabled = false; return; }
    _renderTipusTaskList(tipusId, container, excludeIds);
  });
  input?.addEventListener('blur', () => {
    if (!input.value.trim()) {
      input.hidden = true;
      if (trigger) trigger.hidden = false;
    }
  });

  attachTaskCardHandlers(container, () => _renderTipusTaskList(tipusId, container, excludeIds));
}
